// Membership System Utility Functions
// Common helper functions used across the membership system

/**
 * Format points for display
 * @param {number} points - Raw points value
 * @returns {string} Formatted points string
 */
export const formatPoints = (points) => {
  if (points === null || points === undefined) return '0';
  return points.toLocaleString();
};

/**
 * Format coins for display
 * @param {number} coins - Raw coins value
 * @returns {string} Formatted coins string
 */
export const formatCoins = (coins) => {
  if (coins === null || coins === undefined) return '0';
  return coins.toLocaleString();
};

/**
 * Calculate stamp progress percentage
 * @param {number} stamps - Current stamps collected
 * @param {number} totalStamps - Total stamps needed
 * @returns {number} Progress percentage (0-100)
 */
export const calculateStampProgress = (stamps = 0, totalStamps = 8) => {
  return Math.min((stamps / totalStamps) * 100, 100);
};

/**
 * Check if user has completed stamp collection
 * @param {number} stamps - Current stamps collected
 * @param {number} totalStamps - Total stamps needed
 * @returns {boolean} True if collection is complete
 */
export const isStampCollectionComplete = (stamps = 0, totalStamps = 8) => {
  return stamps >= totalStamps;
};

/**
 * Format timestamp for display
 * @param {Date|FirebaseTimestamp} timestamp - Timestamp to format
 * @returns {string} Formatted date string
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown';
  
  try {
    const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
    return date.toLocaleString();
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid date';
  }
};

// Activity-type rendering: ONE registry only, implemented in
// utils/activityLogRendering.js per app_design_docs/ACTIVITY_LOG_CONTRACT.md.
// A second disagreeing caller-less map (getActivityTypeText) was removed
// 2026-08-27.

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if email is valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Get user initials from name
 * @param {string} name - User's full name
 * @returns {string} User's initials (max 2 characters)
 */
export const getUserInitials = (name) => {
  if (!name) return '?';
  
  const names = name.trim().split(' ');
  if (names.length === 1) {
    return names[0].charAt(0).toUpperCase();
  }
  
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};
